import { useRef, useState } from "react";
import { Routes, Route } from "react-router-dom";
import { ChevronRight, Check, X, Sparkles, Gift } from "lucide-react";
import AppFooter from "../../components/AppFooter/AppFooter";
import VOPPage from "../VOPPage/VOPPage";
import GDPRPage from "../GDPRPage/GDPRPage";
import PrePrevadzkyPage from "../PrePrevadzkyPage/PrePrevadzkyPage";
import AnalyticsLoader from "../../analytics/AnalyticsLoader";
import CompanyRequestForm from "./sections/CompanyRequestForm";
import LandingHeader from "./sections/LandingHeader";
import LandingHead, { PONUKA_ZDARMA } from "./sections/LandingHead";
import HeroMockup from "./sections/HeroMockup";
import Funkcie from "./sections/Funkcie";
import PreKoho from "./sections/PreKoho";
import Kontakt from "./sections/Kontakt";
import Ukazky from "./sections/ukazky/Ukazky";
import "./LandingPage.scss";

/**
 * Marketingova stranka na koreni rozvozy.sk (rezim "root").
 *
 * Vsetky tlacidla vedu na TEN ISTY formular, lisia sa len typom ziadosti:
 *   CHCEM VYSKUSAT -> DEMO   (vlastna demo prevadzka s ukazkovymi datami)
 *   MAM ZAUJEM     -> ZAUJEM (ostra prevadzka)
 * Prihlasenie (do prevadzky podla adresy aj do /sprava) je v LandingHeader.
 *
 * Texty tvrdia len to, co system naozaj vie -- ziadne lehoty. Cena: prvych
 * 10 zaujemcov ma system docasne zdarma (PONUKA_ZDARMA). Je v hero, v pase,
 * v FAQ, v JSON-LD offers aj v public/llms.txt -- pri zmene upravit vsetko naraz.
 */
const PREDTYM = [
  "Objednávky spísané v zošite",
  "Vodič nevie, kto koľko zaplatil a kto koľko dlží",
  "Večer sa ručne sčítava tržba",
  "Zákazníci volajú, čo je dnes v menu",
];

const TERAZ = [
  "Objednávka zapísaná za pár sekúnd",
  "Platba aj dlh sa zapíšu pri doručení",
  "Tržba a saldá sa spočítavajú samé",
  "Menu máte online na vlastnej adrese",
];

const KROKY = [
  { title: "Zákazník objedná", text: "Zavolá a obsluha objednávku zapíše k jeho adrese. Ak máte zapnuté objednávanie cez web, objedná si aj sám." },
  { title: "Prehľad a trasy", text: "Obsluha vidí, koľko ktorého jedla je objednaného a koľko ešte zostáva. Objednávky sa rozdelia medzi vodičov podľa trás." },
  { title: "Vodič doručí", text: "Na mobile potvrdí doručenie a platbu. Tržba, dlhy aj prehľady sú hotové bez prepisovania." },
];

// Prva otazka je definicia v jednej-dvoch vetach -- presne take vety
// generativne vyhladavace (ChatGPT, Perplexity, AI Overviews) citaju doslova.
// Otazky idu aj do JSON-LD FAQPage v LandingHead.
const OTAZKY = [
  { q: "Čo je Rozvozy?", a: "Rozvozy je softvér na evidenciu objednávok a rozvoz obedov pre malé reštaurácie a jedálne. Spája denné menu, objednávky telefonicky aj cez web, trasy vodičov, platby pri doručení a dlhy zákazníkov na jednom mieste. Prevádzkuje ho slovenská spoločnosť Inver s.r.o. so sídlom v Košiciach." },
  { q: "Koľko Rozvozy stoja?", a: `${PONUKA_ZDARMA} Neplatia poplatok za nasadenie ani za používanie, bez ohľadu na počet vodičov či objednávok. Stačí sa ozvať cez tlačidlo Mám záujem.` },
  { q: "Ako si zákazníci objednávajú?", a: "Telefonicky: obsluha objednávku zapíše podľa kódu zákazníka a adresa aj cenník sa doplnia samy. Novinkou je objednávanie cez web, ktoré zatiaľ zapíname postupne. Zákazník sa prihlási na stránke vašej prevádzky, vyberie deň a jedlá a zvolí donášku alebo vyzdvihnutie. Objednať si môže len do času, ktorý nastavíte, a donášku dostane len na adresu, ktorú ste mu povolili. Dovtedy si jedlo vyzdvihne. Objednávanie cez web si zapínate a vypínate sami." },
  { q: "Dá sa platiť online?", a: "Nie. Cez Rozvozy sa neplatí. Zákazník platí pri prevzatí v hotovosti, kartou cez váš terminál, na faktúru alebo ostane dlžný. Vodič alebo obsluha v systéme len zaznačí, ako zaplatil, a tržba aj dlhy sa spočítajú samy." },
  { q: "Musím inštalovať nejaký program alebo aplikáciu?", a: "Nie. Rozvozy sú webová aplikácia, žiadny program sa neinštaluje. Bežia v prehliadači na počítači, tablete aj mobile a vodičom stačí telefón s internetom." },
  { q: "Nahradí Rozvozy zošit alebo Excel s objednávkami?", a: "Áno. Objednávky, adresy, platby aj dlhy, ktoré ste si písali do zošita alebo do Excelu, máte v systéme a tržba sa spočíta sama. Pre účtovníctvo si objednávky viete stiahnuť do Excelu." },
  { q: "Čo dostanem v deme?", a: "Vlastnú demo prevádzku s ukážkovým menu, trasami a zákazníkmi. Prihlásite sa ako administrátor a vyskúšate celý postup od objednávky po doručenie." },
  { q: "Ako rýchlo dostanem prístup?", a: "Každú žiadosť schvaľujeme osobne. Hneď po schválení vám príde e-mail s adresou prevádzky a prihlasovacími údajmi." },
  { q: "Kde budú moji zákazníci objednávať?", a: "Každá prevádzka má vlastnú webovú adresu, napríklad rozvozy.sk/vasa-prevadzka, s vaším názvom, logom a denným menu. Na želanie ju nasadíme aj na vašu vlastnú doménu." },
  { q: "Vidí niekto iný moje dáta?", a: "Nie. Údaje každej prevádzky sú oddelené a prihlásenie do jednej prevádzky neplatí v žiadnej inej." },
];

function Landing() {
  const [typ, setTyp] = useState(null);
  // Po odoslani formular ukaze "Dakujeme". Dalsie kliknutie na tlacidlo ma
  // otvorit NOVY, prazdny formular -- preto novy key (React ho vytvori znova).
  // Pocas vyplnania sa key nemeni, aby klik na tlacidlo nezmazal rozpisane udaje.
  const [formKey, setFormKey] = useState(0);
  const odoslane = useRef(false);
  const formRef = useRef(null);

  const otvor = (novyTyp) => {
    if (odoslane.current) {
      odoslane.current = false;
      setFormKey((k) => k + 1);
    }
    setTyp(novyTyp);
    // formular sa vyrenderuje az po zmene stavu
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  // funkcia, nie komponent -- vnoreny komponent by sa pri kazdom renderi znova pripajal
  const cta = (light = false) => (
    <div className="landing__cta">
      <button type="button" className={`landing__btn landing__btn--outline${light ? " landing__btn--outline-light" : ""}`} onClick={() => otvor("DEMO")}>
        Chcem vyskúšať (DEMO) <ChevronRight size={18} aria-hidden="true" />
      </button>
      <button type="button" className={`landing__btn landing__btn--primary${light ? " landing__btn--light" : ""}`} onClick={() => otvor("ZAUJEM")}>
        Mám záujem <ChevronRight size={18} aria-hidden="true" />
      </button>
    </div>
  );

  return (
    <div className="landing" id="top">
      <LandingHead otazky={OTAZKY} />
      <LandingHeader onCta={() => otvor("DEMO")} />

      <main>
        <section className="landing__hero">
          <div className="landing__hero-text">
            <span className="landing__eyebrow">
              <Sparkles size={14} aria-hidden="true" /> Pre malé reštaurácie a jedálne s rozvozom obedov
            </span>
            <h1 className="landing__title">
              Obedy rozvezené načas. <span>Bez papierikov a zmätkov.</span>
            </h1>
            <p className="landing__subtitle">
              Denné menu, objednávky telefonicky aj cez web, trasy, vodiči a platby — v jednom systéme,
              na vlastnej webovej adrese vašej prevádzky.
            </p>
            <p className="landing__ponuka">
              <span className="landing__ponuka-icon"><Gift size={20} aria-hidden="true" /></span>
              <span>
                <strong>Prvých 10 záujemcov dočasne zdarma</strong>
                Bez poplatku za nasadenie aj za používanie.
              </span>
            </p>
            {cta()}
            <ul className="landing__trust">
              <li><Check size={16} aria-hidden="true" /> Demo s ukážkovými dátami</li>
              <li><Check size={16} aria-hidden="true" /> Bez inštalácie</li>
              <li><Check size={16} aria-hidden="true" /> Na mobile aj počítači</li>
            </ul>
          </div>
          <HeroMockup />
        </section>

        <section className="landing__section landing__compare">
          <h2 className="landing__h2">Poznáte to?</h2>
          <div className="landing__compare-grid">
            <div className="landing__compare-card landing__compare-card--before">
              <h3>Doteraz</h3>
              <ul>
                {PREDTYM.map((t) => (
                  <li key={t}><X size={18} aria-hidden="true" /> {t}</li>
                ))}
              </ul>
            </div>
            <div className="landing__compare-card landing__compare-card--after">
              <h3>S webom Rozvozy</h3>
              <ul>
                {TERAZ.map((t) => (
                  <li key={t}><Check size={18} aria-hidden="true" /> {t}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <Funkcie />

        <section className="landing__section landing__steps-wrap" id="ako-to-funguje">
          <h2 className="landing__h2">Ako to funguje</h2>
          <ol className="landing__steps">
            {KROKY.map(({ title, text }, i) => (
              <li key={title} className="landing__step">
                <span className="landing__step-num">{i + 1}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </li>
            ))}
          </ol>
          <Ukazky />
        </section>

        <PreKoho />

        <section className="landing__band">
          <h2>Vyskúšajte Rozvozy na vlastnej demo prevádzke</h2>
          <p>Vyplníte krátky formulár a po schválení vám prístup príde e-mailom. {PONUKA_ZDARMA}</p>
          {cta(true)}
        </section>

        <div ref={formRef} className="landing__form-anchor">
          {typ && (
            <CompanyRequestForm
              key={formKey}
              typ={typ}
              onTypChange={setTyp}
              onOdoslane={() => { odoslane.current = true; }}
            />
          )}
        </div>

        <section className="landing__section landing__faq" id="otazky">
          <h2 className="landing__h2">Časté otázky</h2>
          {OTAZKY.map(({ q, a }) => (
            <details key={q} className="landing__faq-item">
              <summary>{q} <ChevronRight size={18} aria-hidden="true" /></summary>
              <p>{a}</p>
            </details>
          ))}
        </section>

        <Kontakt />
      </main>
    </div>
  );
}

function LandingPage() {
  return (
    <>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="pre-prevadzky" element={<PrePrevadzkyPage />} />
        <Route path="vop" element={<VOPPage />} />
        <Route path="ochrana-osobnych-udajov" element={<GDPRPage />} />
        <Route path="*" element={<h1 className="landing__not-found">Stránka nenájdená</h1>} />
      </Routes>
      <AppFooter />
      <AnalyticsLoader />
    </>
  );
}

export default LandingPage;
